import {formatCurrency} from "@angular/common";
import {Component, Inject, LOCALE_ID, OnInit, OnDestroy, ChangeDetectionStrategy} from "@angular/core";
import {lastValueFrom} from "rxjs";
import {NzMessageService} from "ng-zorro-antd/message";
import {NgbModal, NgbModalOptions} from "@ng-bootstrap/ng-bootstrap";
import {
    APCheckDto,
    APCheckSignatureDto,
    APCheckPrintPreviewDto,
    ModuleDto,
    PerFormaAPIService,
    APCheckPrintRequestDto,
    APCheckPrintStatusRequestDto
} from "src/app/services/pfapi.service";
import {PerformaModulesService} from "../services/performa-modules.service";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {NzUploadChangeParam, NzUploadFile} from "ng-zorro-antd/upload";

/**
 * This defines the object saved to localStorage.
 *
 * If the user starts a validate/print job and then closes the window
 * or navigates away or any sort of interruption occurs, then the next
 * time they go to AP Check it will reload this object and query
 * the server for the results of the job.
 *
 * Once the job is complete, this item will be removed from localStorage.
 */
export interface APCheckTaskInfo {
    taskId: string;
    bankAccount: number;
    firstCheck: number;
    lastCheck: number;
}

@Component({
    selector: "app-ap",
    templateUrl: "./ap.component.html",
    styleUrl: "./ap.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ApComponent implements OnInit, OnDestroy {
    constructor(
        private readonly api: PerFormaAPIService,
        private readonly message: NzMessageService,
        private readonly ModulesService: PerformaModulesService,
        private readonly sanitizer: DomSanitizer,
        public readonly modal: NgbModal,
        @Inject(LOCALE_ID) public locale: string
    ) {}

    // how often we check for job status after the initial check
    private readonly POLL_INTERVAL_MS = 5000;

    // check status for 10 minutes before giving up
    private readonly MAX_POLL_ATTEMPTS = 120;

    // keep track of how many times we've checked for status
    private pollAttempts = 0;

    // a reference to the polling method
    private pollIntervalId: ReturnType<typeof setInterval> | null = null;

    loading = true;
    printing: boolean; // are we waiting for a print job?
    printStatus = "";
    doneFile = "";
    printError: boolean; // was there an error with the last job?

    // the name of the object stored in localStorage
    taskKeyName = "apc_print_task";

    // if this has a value it means we have a current validate/print job
    taskId: string | null = null;

    apCheckEnabled: boolean; // is AP Check turned on for this client?
    buttonsDisabled = true; // start out with the Validate/Print buttons disabled

    APInfo: APCheckDto = {
        UsesBankAccount: false,
        IsCanadian: false,
        BankAccounts: [],
        Signatures: [],
        DualSignatureThreshold: 99999999,
        PrintPreview: {} as APCheckPrintPreviewDto
    };

    currentSignature: APCheckSignatureDto | null = null;
    currentSignatureImage;
    currentSignatureDisplayImage;

    signatureHasName = false;
    signatureHasImage = false;
    signatureError = "";
    signatureUploadError = "";
    signatureSaveDisabled = false;

    primarySignature: APCheckSignatureDto | null = null;
    secondarySignature: APCheckSignatureDto | null = null;

    pauseMessage =
        "The job is still running but you've stopped checking the status. Reresh the page to continue checking the validate/print status.";
    disabledMessage = "All fields are required";
    printingMessage = "Waiting for print process to finish...";

    bankAccount: number;
    bankTip = "Please enter the single bank account number associated with all checks to be printed.";

    firstCheck: number;
    firstCheckTip =
        "Please enter the first check number to be printed. All checks between the first and last check number will be printed.";

    lastCheck: number;
    lastCheckTip =
        "Please enter the last check number to be printed. All checks between the first and last check number will be printed.";

    primarySigTip = "The primary signature will be applied on all checks.";
    secondarySigTip = `The secondary signature will be applied on all checks in addition to the primary signature if the check amount is over ${formatCurrency(this.APInfo.DualSignatureThreshold, this.locale, "$")}.`;

    modalOpts = {
        size: "md",
        animation: false,
        backdropClass: "backdrop",
        centered: true, // centers it vertically
        backdrop: "static" // don't allow clicking outside of it to close the modal
    } as NgbModalOptions;

    currentSignatureType = "";
    signatureName = "";
    fileList: NzUploadFile[] = [];

    async ngOnInit() {
        try {
            // check if APCheck is enabled
            const modules: ModuleDto[] = (await this.ModulesService.Modules()) ?? [];
            this.apCheckEnabled = modules.some(x => x.Name == "APCheck" && x.IsEnabled);
            this.APInfo = await lastValueFrom(this.api.postApiAPCheckAPCheckInfo());
            this.setupSignatures();

            // check if we have a validate/print job running
            this.WaitForTask();

            // set the focus to the correct input
            setTimeout(() => {
                document.getElementById(this.APInfo.UsesBankAccount ? "bankAccount" : "firstCheck").focus();
            }, 1);
        } catch (e) {
            if (e.error) {
                if (e.error.errorMessage) {
                    this.message.error(e.error.errorMessage);
                } else {
                    this.message.error(e.error);
                }
            } else if (e.status === 0) {
                this.message.error("Client-side or network error occurred.");
            } else {
                const errors = e.error?.ModelState?.status;

                if (e && Array.isArray(errors)) {
                    this.message.error(errors[0]);
                }
            }
        } finally {
            this.loading = false;
        }
    }

    // cleanup the job status poll
    ngOnDestroy(): void {
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
        }
    }

    printOrValidate = async (validateOnly?: boolean) => {
        this.buttonsDisabled = true;
        this.printStatus = "";
        this.printError = false;
        this.doneFile = "";

        try {
            const params = {
                BankAccountNumber: this.APInfo.UsesBankAccount ? this.bankAccount : null,
                FirstCheckNumber: this.firstCheck,
                LastCheckNumber: this.lastCheck,
                ValidateOnly: validateOnly
            } as APCheckPrintRequestDto;

            const result = await lastValueFrom(this.api.postApiAPCheckPrintOrValidate(params));

            if (result.Success) {
                this.AddPrintTask(result.TaskId);
            } else {
                this.message.error(result.ErrorMessage);
            }
        } catch (e) {
            if (e.error) {
                if (e.error.errorMessage) {
                    this.message.error(e.error.errorMessage);
                } else {
                    this.message.error(e.error);
                }
            } else if (e.status === 0) {
                this.message.error("Client-side or network error occurred.");
            } else {
                const errors = e.error?.ModelState?.status;

                if (e && Array.isArray(errors)) {
                    this.message.error(errors[0]);
                }
            }
        } finally {
            this.buttonsDisabled = false;
            this.loading = false;
        }
    };

    setupSignatures = () => {
        this.primarySignature = this.APInfo.Signatures.find(x => x.SignatureNumber == 1);
        this.secondarySignature = this.APInfo.Signatures.find(x => x.SignatureNumber == 2);
    };

    showPrintPreview = () => {
        const tm = this.APInfo.PrintPreview.TopMargin;
        const lm = this.APInfo.PrintPreview.LeftMargin;
        const h = this.APInfo.PrintPreview.Height;
        const w = this.APInfo.PrintPreview.Width;
        const features = `HEIGHT={${h}},WIDTH={${w}},top={${tm}},left={${lm}},toolbar=no,scrollbars=yes,resizable=yes`;

        window.open("/ap/preview", "_blank", features);
    };

    // if all required fields have been filled in and
    // both signatures are present, allow them to
    // click the Validate/Print buttons
    onFieldChange(): void {
        this.buttonsDisabled = !(
            (this.APInfo.UsesBankAccount ? this.bankAccount : true) &&
            this.firstCheck &&
            this.lastCheck &&
            this.primarySignature?.SignatureImage &&
            this.secondarySignature?.SignatureImage
        );
    }

    showSignatureModal = async (tmpl, signatureId: number) => {
        try {
            this.loading = true;

            this.signatureError = "";
            this.signatureUploadError = "";
            this.signatureHasName = false;
            this.signatureHasImage = false;

            const sig = {} as APCheckSignatureDto;

            // load the appropriate signature information
            this.currentSignatureType = signatureId == 1 ? "Primary" : "Secondary";

            sig.SignatureName =
                signatureId == 1
                    ? (this.primarySignature?.SignatureName ?? "")
                    : (this.secondarySignature?.SignatureName ?? "");

            this.signatureHasName = sig.SignatureName.trim().length > 0;

            sig.SignatureNumber = signatureId;

            sig.DisplayImage =
                signatureId == 1 ? this.primarySignature?.DisplayImage : this.secondarySignature?.DisplayImage;

            sig.SignatureImage =
                signatureId == 1 ? this.primarySignature?.SignatureImage : this.secondarySignature?.SignatureImage;

            this.currentSignature = sig;

            this.currentSignatureImage = this.currentSignature.SignatureImage
                ? this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${this.currentSignature.SignatureImage}`)
                : "";

            this.currentSignatureDisplayImage = this.currentSignature.DisplayImage
                ? this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${this.currentSignature.DisplayImage}`)
                : "";

            this.signatureHasImage = this.currentSignature.DisplayImage?.length > 0;

            this.modal.open(tmpl, this.modalOpts);
        } finally {
            this.loading = false;
        }
    };

    saveSignature = async () => {
        if (!this.signatureHasName || !this.signatureHasImage) return;

        this.loading = true;

        try {
            /**
             * Get the signature image and displayimage, splitting on ","
             *
             * Depending if it came from the server, or from a local image
             * upload, it may or may not be prepended with "data:image/xxx;base64,"
             */
            const imageSplit = this.currentSignature.SignatureImage.split(",");
            const displaySplit = this.currentSignature.DisplayImage.split(",");

            const newSig = {
                SignatureNumber: this.currentSignature.SignatureNumber,
                SignatureName: this.currentSignature.SignatureName,
                SignatureImage: imageSplit[1] ?? imageSplit[0], // see comment above
                DisplayImage: displaySplit[1] ?? displaySplit[0] // see comment above
            } as APCheckSignatureDto;

            const sig = await lastValueFrom(this.api.postApiAPCheckUpdateAPCheckSignature(newSig));

            if (this.currentSignature.SignatureNumber == 1) {
                this.primarySignature = JSON.parse(JSON.stringify(sig));
            } else {
                this.secondarySignature = JSON.parse(JSON.stringify(sig));
            }

            this.message.success(
                `Saved ${this.currentSignature.SignatureNumber == 1 ? "Primary" : "Secondary"} Signature.`
            );

            this.onFieldChange();

            this.modal.dismissAll();
        } catch (e) {
            if (e.error) {
                if (e.error.errorMessage) {
                    this.signatureError = e.error.errorMessage;
                } else {
                    this.signatureError = e.error;
                }
            } else if (e.status === 0) {
                this.signatureError = "Client-side or network error occurred.";
            } else {
                const errors = e.error?.ModelState?.status;

                if (e && Array.isArray(errors)) {
                    this.signatureError = errors[0];
                }
            }
        } finally {
            this.loading = false;
        }
    };

    onSignatureNameChange = async (name: string): Promise<void> => {
        const n = name.trim();

        this.signatureError = n.length == 0 ? "signature name is required" : "";
        this.signatureHasName = n.length > 0;
    };

    /**
     * Capture a file upload event just to grab
     * the image bytes (as a base64 string).
     * @param info
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUploadChange(info: NzUploadChangeParam): void {
        this.signatureUploadError = "";

        if (this.fileList.length == 0) return;
        const newFile = this.fileList[this.fileList.length - 1];

        if (newFile.type != "image/png") {
            this.fileList = [];
            this.signatureUploadError = "please select a PNG image";
            return;
        }

        this.getBase64(newFile.originFileObj as File).then((base64: string) => {
            this.currentSignatureImage = this.sanitizer.bypassSecurityTrustUrl(base64);
            this.currentSignatureDisplayImage = this.currentSignatureImage;
            this.currentSignature.SignatureImage = base64;
            this.currentSignature.DisplayImage = base64;
            this.signatureHasImage = true;
            this.fileList = [];
        });
    }

    /**
     * convert file object to a base64 string
     * @param file
     */
    private getBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    /**
     * This method sets up a periodic query to check the status
     * of the current validate/print job. It immediately makes
     * and initial query if there is a current task but afterward
     * it will wait a specified number of seconds before subsequent calls.
     */
    WaitForTask = () => {
        this.LoadPrintTask();

        // no task, so don't do anything
        if (!this.taskId || this.taskId.trim().length === 0) return;

        // if there's already a job running, stop it and start a new one.
        // this will prevent multiple calls happening at the same time.
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
        }

        this.pollAttempts = 0;
        this.printError = false;
        this.doneFile = "";
        this.printing = true;

        // the actual method that makes the api call for job status information
        const checkStatus = () => {
            if (!this.taskId) return;

            const param = {TaskId: this.taskId} as APCheckPrintStatusRequestDto;

            // set the print status text, but depending on how
            // fast the api call is, it may only appear briefly
            this.printStatus = "Checking status...";

            lastValueFrom(this.api.postApiAPCheckGetPrintOrValidateStatus(param))
                .then(result => {
                    this.printError = result.HasError;
                    this.printStatus =
                        result.HasError && result.ErrorMessage
                            ? `${result.Status} - ${result.ErrorMessage}`
                            : result.Status;
                    this.doneFile = result.DoneFile ?? "";

                    if (result.IsComplete) {
                        if (result.HasError) {
                            this.message.error("Finished with errors. See details below.");
                        } else {
                            this.message.success("Finished successfully.");
                        }

                        this.stopPolling();
                        this.RemovePrintTask();
                    } else {
                        this.pollAttempts++;
                        if (this.pollAttempts >= this.MAX_POLL_ATTEMPTS) {
                            this.stopPolling();
                            this.printStatus =
                                "Process is taking longer than expected. Refresh to continue checking the status.";
                        }
                    }
                })
                .catch(e => {
                    this.stopPolling();

                    console.log(e);

                    if (e.error) {
                        if (e.error.errorMessage) {
                            this.message.error(e.error.errorMessage);
                        } else {
                            this.message.error(e.error);
                        }
                    } else if (e.status === 0) {
                        this.message.error("Client-side or network error occurred.");
                    } else {
                        const errors = e.error?.ModelState?.status;
                        if (e && Array.isArray(errors)) {
                            this.message.error(errors[0]);
                        } else {
                            this.message.error("An unexpected error occurred while checking status.");
                        }
                    }
                });
        };

        // run it once right now
        checkStatus();

        // now repeat on the interval
        this.pollIntervalId = setInterval(checkStatus, this.POLL_INTERVAL_MS);
    };

    stopPolling = (userRequested?: boolean) => {
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
        }

        this.pollAttempts = 0;

        // if the user requested to stop checking status,
        // indicate that we are stopping in response to their
        // request.
        if (userRequested) {
            this.printError = true;
            this.printStatus = this.pauseMessage;
        } else {
            this.printing = false;
        }
    };

    // creates an object stored in localStorage
    // and immediately runs the method
    // that checks for a task and starts the
    // polling process for status updates
    AddPrintTask = (taskId: string) => {
        this.taskId = taskId;
        this.printStatus = "";
        this.printError = false;

        const task = {
            taskId: taskId,
            bankAccount: this.bankAccount,
            firstCheck: this.firstCheck,
            lastCheck: this.lastCheck
        } as APCheckTaskInfo;

        localStorage.setItem(this.taskKeyName, JSON.stringify(task));
        this.WaitForTask();
    };

    // remove the task information (this will also cause polling to stop)
    RemovePrintTask = () => {
        this.taskId = null;
        localStorage.removeItem(this.taskKeyName);
    };

    // check if there is information in localStorage and load it
    // so that the process will pick it back up in case they navigated
    // away or had some issue during a print run
    LoadPrintTask = () => {
        const taskString = localStorage.getItem(this.taskKeyName);

        if (!taskString) return;

        if (taskString.trim().length == 0) {
            this.RemovePrintTask();
            return;
        }

        const task: APCheckTaskInfo = JSON.parse(taskString);

        // looks valid, so load the task id and input field values
        // to pick up where they loft off
        if (task) {
            this.taskId = task.taskId.trim();
            this.bankAccount = task.bankAccount;
            this.firstCheck = task.firstCheck;
            this.lastCheck = task.lastCheck;
            this.onFieldChange();
        } else {
            this.RemovePrintTask();
        }
    };
}
